import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MasterManagementService } from '../../../../../management/master-management/master-management.service';
import { CommonService } from '../../../../../../shared/services/common.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-environment',
  templateUrl: './environment.component.html',
  styleUrls: ['./environment.component.css']
})
export class EnvironmentComponent implements OnInit {
  isLoading: boolean = false;
  isVisible: boolean = false;
  customer_id: string;

  public environments: any[] = [];

  isFormModalVisible: boolean = false;
  isEditMode: boolean = false;
  editingEnvId: number | null = null;
  envOptions: any[] = [];

  envForm!: FormGroup;

  listOfColumns = [
    { name: 'Environment Id' },
    { name: 'Environment Name' },
    { name: 'Action' }
  ]


  constructor(
    public route: ActivatedRoute,
    public masterManagementService: MasterManagementService,
    public commonService: CommonService,
    private fb: FormBuilder
  ) {
    this.customer_id = this.route.snapshot.paramMap.get('id') || '';
  }

  ngOnInit() {
    this.getEnvironmentsById();
    this.initializeForm();
  }

  openFormModal(): void {
    this.isEditMode = false;
    this.editingEnvId = null;
    this.envForm.reset();
    this.isFormModalVisible = true;
    this.getEnvOptions();
  }

  openEditModal(env: any): void {
    this.isEditMode = true;
    this.editingEnvId = env.id;
    this.envForm.patchValue({
      environment: env.environment
    });
    this.isFormModalVisible = true;
  }

  initializeForm(): void {
    this.envForm = this.fb.group({
      environment: ['', Validators.required],
    });
  }

  getEnvironmentsById() {
    const payload = {
      customer_id: this.customer_id
    }
    this.isLoading = true;
    this.masterManagementService.getEnvironmentsByCustomerId(payload).subscribe({
      next: (res: any) => {
        this.environments = res?.data;
        this.isLoading = false;
      },
      error: (err: any) => {
        this.isLoading = false;
      }
    });
  }

  getEnvOptions() {
    if (this.envOptions.length > 0) return;

    const payload = {
      customer_id: -1
    }

    this.isLoading = true;
    this.masterManagementService.getEnvironmentsByCustomerId(payload).subscribe({
      next: (res: any) => {
        this.envOptions = res?.data.map((item: any) => ({
          environment: item.environment,
          value: item.id
        }));
        this.isLoading = false;
      },
      error: (err: any) => {
        this.isLoading = false;
      }
    });
  }

  deleteEnv(id: number): void {
    this.isLoading = true;
    this.masterManagementService.deleteEnvironment(id).pipe(untilDestroyed(this)).subscribe({
      next: (response: any) => {
        if (response?.status === 200) {
          this.commonService.message.success('Environment deleted successfully');
          this.getEnvironmentsById();
        } else {
          this.commonService.message.error(response?.message || 'Failed to delete environment');
        }
      },
      error: (error: any) => {
        this.commonService.message.error('An error occurred while deleting the environment');
        console.error('Error:', error);
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  submitForm(): void {
    if (this.isLoading || !this.envForm.valid) return;

    this.isLoading = true;
    const apiCall = this.isEditMode ? this.handleUpdate() : this.handleAdd();

    if (!apiCall) {
      this.isLoading = false;
      return;
    }

    apiCall.pipe(untilDestroyed(this)).subscribe({
      next: (response: any) => this.handleSuccess(response),
      error: (error: any) => this.handleError(error)
    });
  }

  private handleAdd() {
    const payload = {
      environment: this.envForm.value.environment,
      customer_id: this.customer_id
    };
    return this.masterManagementService.addEnvironment(payload);
  }

  private handleUpdate() {
    if (!this.editingEnvId) return null;

    const payload = {
      environment: this.envForm.value.environment
    };
    return this.masterManagementService.updateEnvironment(this.editingEnvId, payload);
  }

  private handleSuccess(response: any): void {
    if (response?.status === 200) {
      this.commonService.message.success(
        this.isEditMode ? 'Environment updated successfully' : 'Environment added successfully'
      );
      this.isFormModalVisible = false;
      this.envForm.reset();
      this.isEditMode = false;
      this.editingEnvId = null;
      this.getEnvironmentsById();
    } else {
      this.commonService.message.error(
        response?.message || (this.isEditMode ? 'Failed to update environment' : 'Failed to add environment')
      );
    }
    this.isLoading = false;
  }

  private handleError(error: any): void {
    this.commonService.message.error(
      this.isEditMode ? 'An error occurred while updating the environment' : 'An error occurred while adding the environment'
    );
    console.error('Error:', error);
    this.isLoading = false;
  }
}


